/**
 * Every lazily-loaded page component, in one module.
 *
 * Extracted from `App.tsx` when adding the structured-protocol route took that
 * file past its 1000-line gate. It is a PURE MOVE: the same
 * `lazyWithRetry(() => import(...))` calls, in the same order, re-exported and
 * imported back. Nothing about chunking or preloading changes, because the
 * dynamic `import()` specifiers are what the bundler splits on and they are
 * untouched, and `lazyWithRetry` still owns the retry and preload behaviour.
 */
import { lazyWithRetry } from './utils/lazyWithRetry';

/** How long a CRITICAL route may spend loading before `lazyWithRetry` gives up
 * and retries. Moved here with the declarations that read it. */
export const CRITICAL_ROUTE_LOAD_TIMEOUT_MS = 10_000;

export const PublicSharePage = lazyWithRetry(() => import('./pages/PublicShare'));
export const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
export const WorkspaceCanvasPage = lazyWithRetry(() => import('./pages/WorkspaceCanvas'));
export const ProtocolLibraryGridPage = lazyWithRetry(() => import('./pages/ProtocolLibraryGrid'));
export const ProtocolDetail = lazyWithRetry(() => import('./pages/ProtocolDetail'));
export const ProtocolRunPage = lazyWithRetry(() => import('./pages/ProtocolRun/ProtocolRunPage'));
export const EquipmentDetail = lazyWithRetry(() => import('./pages/EquipmentDetail'));
export const Graph = lazyWithRetry(() => import('./pages/Graph'));
export const NoteDetail = lazyWithRetry(() => import('./pages/NoteDetail'));
export const AttachmentDetail = lazyWithRetry(() => import('./pages/AttachmentDetail'));
export const SpreadsheetDetail = lazyWithRetry(() => import('./pages/SpreadsheetDetail'));
export const LinkRedirect = lazyWithRetry(() => import('./pages/LinkRedirect'));
export const AccountSettings = lazyWithRetry(() => import('./pages/AccountSettings'));
export const InternalAdmin = lazyWithRetry(() => import('./pages/InternalAdmin'));
export const TrashGridPage = lazyWithRetry(() => import('./pages/TrashGrid'));
export const Audit = lazyWithRetry(() => import('./pages/Audit'));
export const Login = lazyWithRetry(() => import('./pages/Login'), {
  activeLoadTimeoutMs: CRITICAL_ROUTE_LOAD_TIMEOUT_MS,
  reloadKey: 'Login',
});
export const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'));
export const AuthAction = lazyWithRetry(() => import('./pages/AuthAction'));
export const VerifyEmail = lazyWithRetry(() => import('./pages/VerifyEmail'));
export const RecoverEmail = lazyWithRetry(() => import('./pages/RecoverEmail'));
export const EmailVerificationPrompt = lazyWithRetry(
  () => import('./pages/EmailVerificationPrompt'),
);
export const WorkosSignIn = lazyWithRetry(() =>
  import('./pages/auth/WorkosSignInPage').then((m) => ({ default: m.WorkosSignInPage })),
);
export const NotFound = lazyWithRetry(() => import('./pages/NotFound'));
export const Onboarding = lazyWithRetry(() => import('./pages/onboarding/OnboardingRouter'), {
  activeLoadTimeoutMs: CRITICAL_ROUTE_LOAD_TIMEOUT_MS,
  reloadKey: 'OnboardingRouter',
});
export const ConnectorOAuthCallback = lazyWithRetry(() =>
  import('./pages/ConnectorOAuthCallback').then((m) => ({ default: m.ConnectorOAuthCallback })),
);
export const MagicLinkRedemption = lazyWithRetry(
  () =>
    import('./pages/mobile/MagicLinkRedemption').then((m) => ({
      default: m.MagicLinkRedemption,
    })),
  {
    activeLoadTimeoutMs: CRITICAL_ROUTE_LOAD_TIMEOUT_MS,
    reloadKey: 'MagicLinkRedemption',
  },
);
export const MobileFoldersWorkspacePage = lazyWithRetry(
  () => import('./pages/mobile/workspace/MobileWorkspacePage'),
  {
    activeLoadTimeoutMs: CRITICAL_ROUTE_LOAD_TIMEOUT_MS,
    reloadKey: 'MobileWorkspacePage',
  },
);
export const LiveSessionPage = lazyWithRetry(() => import('./pages/mobile/LiveSessionPage'));
export const LiveCallPage = lazyWithRetry(() => import('./pages/LiveCall'));
export const MobileBirdPage = lazyWithRetry(() => import('./pages/mobile/MobileBirdPage'));
